// ----------------------------------------------------------------------------
// song-file.js — Save/Load full DAWSEUR songs with embedded samples
//
// Format: .dws (Dawseur Song)
// Structure: File header + JSON header + binary sample data
//
// [File header (32 bytes, fixed): "©AZOPSOFT DAWSER V0.1\0..." padded with NULs]
// [JSON header length (4 bytes, little-endian uint32)]
// [JSON header bytes]
// [sample data blobs...]
//
// The JSON header contains all song state (tracks, events, automations,
// step sequencer, slicer, piano roll, mixer, FX, master settings).
// Sample data is embedded as WAV blobs, referenced by index from the header.
//
// ----------------------------------------------------------------------------

const { audioBufferToWav } = require('./offline-render.js');

const DWS_MAGIC = 'DWSONG';
const DWS_VERSION = 1;

// Fixed-size file header (32 bytes): copyright + app identifier + version
const FILE_HEADER = '\u00A9AZOPSOFT DAWSER V0.1'; // ©AZOPSOFT DAWSER V0.1
const FILE_HEADER_SIZE = 32;

// ---- Save: serialize everything to a single ArrayBuffer ----
// opts: { engine, timeline, trackList, automation, stepSequencer, slicer, pianoRoll, getMixerTracks, bucket }
async function saveSong(opts) {
   const {
      engine, timeline, trackList, automation,
      stepSequencer, slicer, pianoRoll, getMixerTracks, bucket
   } = opts;

   // ---- Collect all unique samples (by ID) ----
   // Samples come from: timeline audio events, step seq channels, slicer sequences, piano roll patterns
   const sampleMap = new Map(); // sampleId -> { id, name, wavBytes }
   const audioBufferSamples = new Map(); // AudioBuffer reference -> sampleId

   function addSampleById(sampleId, name) {
      if (!sampleId || sampleMap.has(sampleId)) return;
      // Try to get the raw ArrayBuffer from bucket
      let arrayBuffer = null;
      if (bucket && bucket.items) {
         const item = bucket.items.find(it => it.id === sampleId);
         if (item) {
            if (item.wav instanceof ArrayBuffer) arrayBuffer = item.wav;
            else if (item.wav instanceof Uint8Array) arrayBuffer = item.wav.buffer;
            else if (item.wavLike instanceof ArrayBuffer) arrayBuffer = item.wavLike;
            else if (item.wavLike instanceof Uint8Array) arrayBuffer = item.wavLike.buffer;
            else if (item.data instanceof ArrayBuffer) arrayBuffer = item.data;
            else if (item.data instanceof Uint8Array) arrayBuffer = item.data.buffer;
            name = item.name || name || sampleId;
         }
      }
      if (arrayBuffer) {
         // Store as bytes (copy to ensure we have a clean ArrayBuffer)
         const bytes = arrayBuffer.slice(0);
         sampleMap.set(sampleId, { id: sampleId, name: name || sampleId, wavBytes: bytes });
      }
   }

   async function addSampleByBuffer(buffer, name) {
      if (!buffer) return null;
      // Check if we've already seen this buffer reference
      if (audioBufferSamples.has(buffer)) return audioBufferSamples.get(buffer);
      // Generate a synthetic ID for timeline audio buffers
      const sampleId = 'buf_' + (audioBufferSamples.size + 1);
      const wavBlob = audioBufferToWav(buffer);
      // audioBufferToWav returns a Blob — convert to ArrayBuffer
      const wavBytes = wavBlob instanceof Blob ? await wavBlob.arrayBuffer() : wavBlob;
      sampleMap.set(sampleId, { id: sampleId, name: name || sampleId, wavBytes: wavBytes });
      audioBufferSamples.set(buffer, sampleId);
      return sampleId;
   }

   // ---- Serialize timeline tracks + events ----
   const tlTracks = [];
   if (timeline && timeline.state.tracks) {
      for (const tlTrack of timeline.state.tracks) {
         const trackEvents = [];
         for (const ev of tlTrack.events) {
            const serEv = {
               startBeat: ev.startBeat,
               lengthBeats: ev.lengthBeats,
               volume: ev.volume,
               stretched: ev.stretched || false,
               fadeInBeats: ev.fadeInBeats || 0,
               fadeOutBeats: ev.fadeOutBeats || 0,
               fadeInCp1X: ev.fadeInCp1X, fadeInCp1Y: ev.fadeInCp1Y,
               fadeInCp2X: ev.fadeInCp2X, fadeInCp2Y: ev.fadeInCp2Y,
               fadeOutCp1X: ev.fadeOutCp1X, fadeOutCp1Y: ev.fadeOutCp1Y,
               fadeOutCp2X: ev.fadeOutCp2X, fadeOutCp2Y: ev.fadeOutCp2Y,
            };
            if (ev.acidMeta) serEv.acidMeta = ev.acidMeta;

            if (ev.type === 'stepseq' || ev.type === 'slicer' || ev.type === 'pianoroll') {
               serEv.type = ev.type;
               if (ev.sequence) {
                  // Deep copy sequence, strip audioBuffer
                  const { audioBuffer, ...seqRest } = ev.sequence;
                  serEv.sequence = seqRest;
                  // Add sample reference
                  if (ev.type === 'stepseq') {
                     for (const ch of (seqRest.channels || [])) {
                        addSampleById(ch.sampleId, ch.sampleName);
                     }
                  } else if (ev.type === 'slicer') {
                     addSampleById(seqRest.sampleId, seqRest.sampleName);
                  }
               }
               if (ev.pattern) {
                  const { audioBuffer, _drawGrid, _animatePlayhead, ...patRest } = ev.pattern;
                  serEv.pattern = patRest;
                  addSampleById(patRest.sampleId, patRest.sampleName);
               }
            } else {
               // Audio event — embed the buffer
               if (ev.buffer) {
                  serEv.sampleRef = await addSampleByBuffer(ev.buffer, tlTrack.name + '_audio');
               }
            }
            trackEvents.push(serEv);
         }
         tlTracks.push({
            id: tlTrack.id,
            name: tlTrack.name,
            color: tlTrack.color,
            events: trackEvents,
         });
      }
   }

   // ---- Serialize track-list tracks (volume, pan, mute, solo, etc.) ----
   const tracks = trackList ? trackList.getTracks().map(t => ({
      id: t.id,
      name: t.name,
      colorVar: t.colorVar,
      color: t.color,
      volume: t.volume,
      pan: t.pan,
      muted: t.muted,
      solo: t.solo,
      fxEnabled: t.fxEnabled,
      pitchShift: t.pitchShift || 0,
   })) : [];

   // ---- Serialize mixer state ----
   const mixerTracks = getMixerTracks ? getMixerTracks().map(t => ({
      id: t.id,
      mixerVolume: t.mixerVolume,
      mixerPanVal: t.mixerPanVal,
      eqLowGain: t.eqLowGain,
      eqMidGain: t.eqMidGain,
      eqHiGain: t.eqHiGain,
   })) : [];

   // ---- Serialize master settings ----
   const master = {};
   if (engine) {
      master.masterVolume = engine.state.masterVolume !== undefined ? engine.state.masterVolume : 1.0;
      master.masterPan = engine.state.masterPan !== undefined ? engine.state.masterPan : 0;
      master.masterCompEnabled = engine.state.masterCompEnabled || false;
      master.masterCompThreshold = engine.state.masterCompThreshold !== undefined ? engine.state.masterCompThreshold : -6;
      master.masterCompRatio = engine.state.masterCompRatio !== undefined ? engine.state.masterCompRatio : 20;
      master.masterCompKnee = engine.state.masterCompKnee !== undefined ? engine.state.masterCompKnee : 0;
      master.masterCompAttack = engine.state.masterCompAttack !== undefined ? engine.state.masterCompAttack : 0.003;
      master.masterCompRelease = engine.state.masterCompRelease !== undefined ? engine.state.masterCompRelease : 0.1;
      master.masterEqLow = engine.state.masterEqLow !== undefined ? engine.state.masterEqLow : 0;
      master.masterEqMid = engine.state.masterEqMid !== undefined ? engine.state.masterEqMid : 0;
      master.masterEqHi = engine.state.masterEqHi !== undefined ? engine.state.masterEqHi : 0;
   }

   // ---- Serialize FX chains per track ----
   const fxChains = {};
   if (engine) {
      for (const track of tracks) {
         const chain = engine.getTrackFx(track.id);
         if (chain && chain.length > 0) {
            fxChains[track.id] = chain.map(fx => {
               const def = fx._def || {};
               const params = {};
               if (def.inputs && fx._controls) {
                  for (const input of def.inputs) {
                     const el = fx._controls[input.name];
                     if (el) {
                        params[input.name] = el.type === 'select-one' ? el.value : parseFloat(el.value);
                     }
                  }
               }
               return {
                  type: def.name || 'unknown',
                  bypassed: !!fx._bypassed,
                  params: params,
               };
            });
         }
      }
   }

   // ---- Serialize automations ----
   const autoClips = [];
   if (automation && automation.getAllClips) {
      for (const clip of automation.getAllClips()) {
         autoClips.push({
            trackId: clip.trackId,
            paramName: clip.paramName,
            points: clip.points,
            lengthBeats: clip.lengthBeats,
            startBeat: clip.startBeat,
            min: clip.min,
            max: clip.max,
            step: clip.step,
            isKnob: clip.isKnob || false,
            knobId: clip.knobId || null,
         });
      }
   }
   // Also serialize timeline autoTracks (per-segment automations)
   const tlAutoTracks = [];
   if (timeline && timeline.state.autoTracks) {
      for (const a of timeline.state.autoTracks) {
         tlAutoTracks.push({
            id: a.id,
            parentId: a.parentId,
            paramName: a.paramName,
            points: a.points ? a.points.map(p => ({ x: p.x, y: p.y })) : [],
            startBeat: a.startBeat || 0,
            lengthBeats: a.lengthBeats || 4,
            min: a.min !== undefined ? a.min : 0,
            max: a.max !== undefined ? a.max : 1,
         });
      }
   }

   // ---- Serialize step sequencer sequences ----
   const stepSeqSequences = stepSequencer ? stepSequencer.getSequences() : [];
   for (const seq of stepSeqSequences) {
      for (const ch of (seq.channels || [])) {
         addSampleById(ch.sampleId, ch.sampleName);
      }
   }

   // ---- Serialize slicer sequences ----
   const slicerSequences = slicer ? slicer.getSequences() : [];
   for (const seq of slicerSequences) {
      addSampleById(seq.sampleId, seq.sampleName);
   }

   // ---- Serialize piano roll patterns ----
   const pianoRollPatterns = pianoRoll ? pianoRoll.getPatterns() : [];
   for (const pat of pianoRollPatterns) {
      addSampleById(pat.sampleId, pat.sampleName);
   }

   // ---- Serialize timeline state ----
   const tlState = {};
   if (timeline) {
      tlState.bpm = timeline.state.bpm || 120;
      tlState.beatsPerMeasure = timeline.state.beatsPerMeasure || 4;
      tlState.totalBeats = timeline.state.totalBeats || 64;
      tlState.loopEnabled = timeline.state.loopEnabled || false;
      tlState.loopStart = timeline.state.loopStart || 0;
      tlState.loopEnd = timeline.state.loopEnd || 0;
      // View state (zoom, scroll, snap, tool)
      tlState.pxPerBeat = timeline.state.pxPerBeat || 30;
      tlState.scrollLeft = timeline.state.scrollLeft || 0;
      tlState.scrollTop = timeline.state.scrollTop || 0;
      tlState.trackHeight = timeline.state.trackHeight || 80;
      tlState.snapEnabled = timeline.state.snapEnabled !== undefined ? timeline.state.snapEnabled : true;
      tlState.tool = timeline.state.tool || 'select';
   }

   // ---- Build the sample table ----
   const sampleTable = [];
   let sampleOffset = 0;
   for (const [id, sample] of sampleMap) {
      sampleTable.push({
         id: id,
         name: sample.name,
         offset: sampleOffset,
         size: sample.wavBytes.byteLength,
      });
      sampleOffset += sample.wavBytes.byteLength;
   }

   // ---- Build the song header ----
   const header = {
      magic: DWS_MAGIC,
      version: DWS_VERSION,
      timeline: tlState,
      tracks: tracks,
      timelineTracks: tlTracks,
      mixerTracks: mixerTracks,
      master: master,
      fxChains: fxChains,
      autoClips: autoClips,
      timelineAutoTracks: tlAutoTracks,
      stepSeqSequences: stepSeqSequences,
      slicerSequences: slicerSequences,
      pianoRollPatterns: pianoRollPatterns,
      samples: sampleTable,
   };

   // ---- Serialize to binary: [fileHeader(32)] [headerLen(4)] [headerJSON] [sample blobs...] ----
   const headerJson = JSON.stringify(header);
   const headerBytes = new TextEncoder().encode(headerJson);
   const headerLen = new Uint8Array(4);
   new DataView(headerLen.buffer).setUint32(0, headerBytes.byteLength, true);

   // File header: fixed 32-byte string, NUL-padded
   const fileHeaderBytes = new Uint8Array(FILE_HEADER_SIZE);
   const fhStr = new TextEncoder().encode(FILE_HEADER);
   fileHeaderBytes.set(fhStr.subarray(0, Math.min(fhStr.length, FILE_HEADER_SIZE)));

   // Build the final blob as an array of parts (avoids "source array is too long")
   // Use Uint8Array views (not .buffer) to avoid including extra bytes from over-allocated ArrayBuffers
   const parts = [fileHeaderBytes, headerLen, headerBytes];
   for (const [id, sample] of sampleMap) {
      // Ensure each sample is a Uint8Array view (works with both ArrayBuffer and Uint8Array)
      if (sample.wavBytes instanceof ArrayBuffer) {
         parts.push(new Uint8Array(sample.wavBytes));
      } else {
         parts.push(sample.wavBytes);
      }
   }

   return new Blob(parts, { type: 'application/octet-stream' });
}

// ---- Load: parse a .dws file and reconstruct all state ----
// opts: { engine, timeline, trackList, automation, stepSequencer, slicer, pianoRoll, fxRack, mixer, bucket, ctx }
async function loadSong(arrayBuffer, opts) {
   const {
      engine, timeline, trackList, automation,
      stepSequencer, slicer, pianoRoll, fxRack, mixer, ctx
   } = opts;

   // ---- Parse the file header (first 32 bytes) ----
   const view = new DataView(arrayBuffer);
   const fileHeaderBytes = new Uint8Array(arrayBuffer, 0, FILE_HEADER_SIZE);
   const fileHeaderStr = new TextDecoder().decode(fileHeaderBytes).replace(/\0+$/, '');

   // Check for file header — if present, skip it; otherwise treat as legacy file
   let offset = 0;
   if (fileHeaderStr.startsWith('\u00A9AZOPSOFT DAWSER')) {
      offset = FILE_HEADER_SIZE;
   }

   // ---- Parse the binary header ----
   const headerLen = view.getUint32(offset, true);
   offset += 4;
   const headerBytes = new Uint8Array(arrayBuffer, offset, headerLen);
   const headerJson = new TextDecoder().decode(headerBytes);
   const header = JSON.parse(headerJson);

   if (header.magic !== DWS_MAGIC) {
      throw new Error('Not a valid .dws file (bad magic: ' + header.magic + ')');
   }

   // ---- Extract embedded samples ----
   const sampleData = new Map(); // sampleId -> ArrayBuffer (WAV)
   const sampleNames = new Map(); // sampleId -> name
   const sampleSectionStart = offset + headerLen;
   for (const sample of (header.samples || [])) {
      const start = sampleSectionStart + sample.offset;
      const end = start + sample.size;
      if (end > arrayBuffer.byteLength || start < 0) {
         console.error('[dws] Sample ' + sample.id + ' out of bounds! start=' + start + ' end=' + end + ' fileSize=' + arrayBuffer.byteLength);
         continue;
      }
      const wavBytes = arrayBuffer.slice(start, end);
      if (wavBytes.byteLength < 12) {
         console.error('[dws] Sample ' + sample.id + ' too small: ' + wavBytes.byteLength + ' bytes');
         continue;
      }
      sampleData.set(sample.id, wavBytes);
      sampleNames.set(sample.id, sample.name);
   }

   // ---- Decode all samples into AudioBuffers ----
   const audioBuffers = new Map(); // sampleId -> AudioBuffer
   for (const [sampleId, wavBytes] of sampleData) {
      try {
         const buffer = await ctx.decodeAudioData(wavBytes.slice(0));
         audioBuffers.set(sampleId, buffer);
      } catch (e) {
         console.error('[dws] Failed to decode sample ' + sampleId + ' (' + wavBytes.byteLength + ' bytes):', e);
      }
   }

   // ---- Inject samples into bucket (so existing sample loader can find them) ----
   if (opts.bucket && opts.bucket.items) {
      for (const [sampleId, wavBytes] of sampleData) {
         // If the bucket doesn't already have this sample, add it
         if (!opts.bucket.items.find(it => it.id === sampleId)) {
            opts.bucket.items.push({
               id: sampleId,
               name: sampleNames.get(sampleId) || sampleId,
               wav: wavBytes,
            });
         }
      }
   }

   // ---- Also inject into engine's MIDI sample cache ----
   if (engine && engine.setMIDISampleBuffer) {
      for (const [sampleId, buffer] of audioBuffers) {
         engine.setMIDISampleBuffer(sampleId, buffer);
      }
   } else if (engine) {
      // Fallback: inject into the midi sample cache if accessible
      for (const [sampleId, buffer] of audioBuffers) {
         if (engine.midiSampleCache) {
            engine.midiSampleCache.set(sampleId, buffer);
         }
      }
   }

   // ---- Stop playback if running ----
   if (engine && engine.state.isPlaying) {
      engine.stop();
   }

   // ---- Clear existing tracks ----
   if (trackList && trackList.getTracks) {
      const existing = trackList.getTracks();
      for (const t of existing) {
         if (trackList.removeTrack) trackList.removeTrack(t.id);
      }
   }

   // ---- Reconstruct tracks ----
   for (const trackDef of (header.tracks || [])) {
      if (trackList && trackList.addTrack) {
         const t = trackList.addTrack(trackDef.name || 'Track', { id: trackDef.id });
         t.colorVar = trackDef.colorVar || t.colorVar;
         t.color = trackDef.color || t.color;
         t.volume = trackDef.volume !== undefined ? trackDef.volume : 0.8;
         t.pan = trackDef.pan !== undefined ? trackDef.pan : 0;
         t.muted = trackDef.muted || false;
         t.solo = trackDef.solo || false;
         t.fxEnabled = trackDef.fxEnabled !== undefined ? trackDef.fxEnabled : true;
         t.pitchShift = trackDef.pitchShift || 0;
      }
   }

   // ---- Set engine track params (mixer, EQ, pitch, volume, pan, mute, solo) ----
   if (engine) {
      for (const mt of (header.mixerTracks || [])) {
         const engTrack = engine.getTrack(mt.id);
         if (engTrack) {
            if (mt.mixerVolume !== undefined) engTrack.mixerVolume = mt.mixerVolume;
            if (mt.mixerPanVal !== undefined) engTrack.mixerPanVal = mt.mixerPanVal;
            if (mt.eqLowGain !== undefined) engTrack.eqLowGain = mt.eqLowGain;
            if (mt.eqMidGain !== undefined) engTrack.eqMidGain = mt.eqMidGain;
            if (mt.eqHiGain !== undefined) engTrack.eqHiGain = mt.eqHiGain;
         }
         // Apply to audio nodes
         if (engine.setTrackMixerVolume) engine.setTrackMixerVolume(mt.id, mt.mixerVolume !== undefined ? mt.mixerVolume : 0.6);
         if (engine.setTrackMixerPan) engine.setTrackMixerPan(mt.id, mt.mixerPanVal || 0);
         if (engine.setTrackEq) {
            engine.setTrackEq(mt.id, 'low', mt.eqLowGain || 0);
            engine.setTrackEq(mt.id, 'mid', mt.eqMidGain || 0);
            engine.setTrackEq(mt.id, 'high', mt.eqHiGain || 0);
         }
      }
      // Apply track-list volume, pan, mute, solo, and pitch to engine
      for (const trackDef of (header.tracks || [])) {
         const engTrack = engine.getTrack(trackDef.id);
         if (engTrack) {
            engTrack.volume = trackDef.volume !== undefined ? trackDef.volume : 0.8;
            engTrack.pan = trackDef.pan !== undefined ? trackDef.pan : 0;
            engTrack.muted = trackDef.muted || false;
            engTrack.solo = trackDef.solo || false;
            // Apply to audio nodes
            if (engine.setTrackVolume) engine.setTrackVolume(trackDef.id, engTrack.volume);
            if (engine.setTrackPan) engine.setTrackPan(trackDef.id, engTrack.pan);
            if (engine.setTrackMute) engine.setTrackMute(trackDef.id, engTrack.muted);
            if (engine.setTrackSolo) engine.setTrackSolo(trackDef.id, engTrack.solo);
         }
         if (trackDef.pitchShift) engine.setTrackPitch(trackDef.id, trackDef.pitchShift);
      }
   }

   // ---- Set master settings ----
   const m = (engine && header.master) ? header.master : null;
   if (m) {
      if (engine.setMasterVolume) engine.setMasterVolume(m.masterVolume !== undefined ? m.masterVolume : 1.0);
      if (engine.setMasterPan) engine.setMasterPan(m.masterPan || 0);
      if (engine.setMasterEq) {
         engine.setMasterEq('low', m.masterEqLow || 0);
         engine.setMasterEq('mid', m.masterEqMid || 0);
         engine.setMasterEq('high', m.masterEqHi || 0);
      }
      if (engine.setMasterCompParam) {
         engine.setMasterCompParam('threshold', m.masterCompThreshold !== undefined ? m.masterCompThreshold : -6);
         engine.setMasterCompParam('ratio', m.masterCompRatio !== undefined ? m.masterCompRatio : 20);
         engine.setMasterCompParam('knee', m.masterCompKnee || 0);
         engine.setMasterCompParam('attack', m.masterCompAttack !== undefined ? m.masterCompAttack : 0.003);
         engine.setMasterCompParam('release', m.masterCompRelease !== undefined ? m.masterCompRelease : 0.1);
      }
      if (engine.setMasterComp) engine.setMasterComp(m.masterCompEnabled || false);

      // Update mixer UI knobs to reflect loaded master settings
      if (typeof window !== 'undefined' && window._knobRegistry) {
         const updateKnob = (id, val) => {
            const k = window._knobRegistry.get(id);
            if (k) k.setValueVisual(val);
         };
         updateKnob('mixer_master_vol', m.masterVolume !== undefined ? m.masterVolume : 1.0);
         updateKnob('mixer_master_pan', m.masterPan || 0);
         updateKnob('mixer_master_eq_low', m.masterEqLow || 0);
         updateKnob('mixer_master_eq_mid', m.masterEqMid || 0);
         updateKnob('mixer_master_eq_hi', m.masterEqHi || 0);
         updateKnob('mixer_master_comp_threshold', m.masterCompThreshold !== undefined ? m.masterCompThreshold : -6);
         updateKnob('mixer_master_comp_ratio', m.masterCompRatio !== undefined ? m.masterCompRatio : 20);
         updateKnob('mixer_master_comp_knee', m.masterCompKnee || 0);
         updateKnob('mixer_master_comp_attack', m.masterCompAttack !== undefined ? m.masterCompAttack : 0.003);
         updateKnob('mixer_master_comp_release', m.masterCompRelease !== undefined ? m.masterCompRelease : 0.1);
      }
   }

   // ---- Reconstruct FX chains ----
   if (fxRack && engine) {
      for (const [trackId, chainDef] of Object.entries(header.fxChains || {})) {
         // Select the track in FX rack
         if (fxRack.setCurrentTrack) fxRack.setCurrentTrack(trackId);
         for (const fxDef of chainDef) {
            // Find the FX definition by name and add it
            if (fxRack.addEffectByName) {
               fxRack.addEffectByName(fxDef.type, fxDef.params || {}, fxDef.bypassed || false);
            }
         }
      }
   }

   // ---- Reconstruct timeline tracks + events ----
   if (timeline) {
      // Set BPM and transport state
      if (header.timeline) {
         timeline.state.bpm = header.timeline.bpm || 120;
         timeline.state.beatsPerMeasure = header.timeline.beatsPerMeasure || 4;
         timeline.state.totalBeats = header.timeline.totalBeats || 64;
         timeline.state.loopEnabled = header.timeline.loopEnabled || false;
         timeline.state.loopStart = header.timeline.loopStart || 0;
         timeline.state.loopEnd = header.timeline.loopEnd || 0;
         // Restore view state
         if (header.timeline.pxPerBeat) timeline.state.pxPerBeat = header.timeline.pxPerBeat;
         if (header.timeline.scrollLeft !== undefined) timeline.state.scrollLeft = header.timeline.scrollLeft;
         if (header.timeline.scrollTop !== undefined) timeline.state.scrollTop = header.timeline.scrollTop;
         if (header.timeline.trackHeight) timeline.state.trackHeight = header.timeline.trackHeight;
         if (header.timeline.snapEnabled !== undefined) timeline.state.snapEnabled = header.timeline.snapEnabled;
         if (header.timeline.tool) timeline.state.tool = header.timeline.tool;
      }

      // Build timeline tracks with events
      const tlTracks = (header.timelineTracks || []).map(tlTrack => {
         const events = (tlTrack.events || []).map(ev => {
            const reconstructed = {
               startBeat: ev.startBeat,
               lengthBeats: ev.lengthBeats,
               volume: ev.volume,
               stretched: ev.stretched || false,
               fadeInBeats: ev.fadeInBeats || 0,
               fadeOutBeats: ev.fadeOutBeats || 0,
               fadeInCp1X: ev.fadeInCp1X, fadeInCp1Y: ev.fadeInCp1Y,
               fadeInCp2X: ev.fadeInCp2X, fadeInCp2Y: ev.fadeInCp2Y,
               fadeOutCp1X: ev.fadeOutCp1X, fadeOutCp1Y: ev.fadeOutCp1Y,
               fadeOutCp2X: ev.fadeOutCp2X, fadeOutCp2Y: ev.fadeOutCp2Y,
            };
            if (ev.acidMeta) reconstructed.acidMeta = ev.acidMeta;

            if (ev.type === 'stepseq' || ev.type === 'slicer' || ev.type === 'pianoroll') {
               reconstructed.type = ev.type;
               if (ev.sequence) {
                  // Reconstruct sequence with audioBuffer if available
                  const seqData = { ...ev.sequence };
                  if (seqData.sampleId && audioBuffers.has(seqData.sampleId)) {
                     seqData.audioBuffer = audioBuffers.get(seqData.sampleId);
                  }
                  // For step seq, channels reference samples by ID (loaded via bucket)
                  reconstructed.sequence = seqData;
               }
               if (ev.pattern) {
                  const patData = { ...ev.pattern };
                  if (patData.sampleId && audioBuffers.has(patData.sampleId)) {
                     patData.audioBuffer = audioBuffers.get(patData.sampleId);
                  }
                  reconstructed.pattern = patData;
               }
            } else {
               // Audio event — restore buffer from embedded sample
               if (ev.sampleRef && audioBuffers.has(ev.sampleRef)) {
                  reconstructed.buffer = audioBuffers.get(ev.sampleRef);
               }
            }
            return reconstructed;
         });
         return {
            id: tlTrack.id,
            name: tlTrack.name,
            color: tlTrack.color,
            events: events,
         };
      });

      timeline.setTracks(tlTracks);

      // Reconstruct autoTracks
      if (header.timelineAutoTracks) {
         timeline.state.autoTracks = header.timelineAutoTracks.map(a => ({ ...a }));
      }
   }

   // ---- Reconstruct automations ----
   if (automation) {
      // Load the Map clips (original automation clips)
      if (automation.loadClips && header.autoClips) {
         const clips = header.autoClips.map(c => ({
            id: c.trackId + ':' + c.paramName,
            trackId: c.trackId,
            paramName: c.paramName,
            points: c.points || [{ x: 0, y: 0 }, { x: 1, y: 0 }],
            lengthBeats: c.lengthBeats || 4,
            startBeat: c.startBeat || 0,
            min: c.min,
            max: c.max,
            step: c.step,
            isKnob: c.isKnob || false,
            knobId: c.knobId || null,
         }));
         automation.loadClips(clips);
      }
   }

   // ---- Reconstruct step sequencer sequences ----
   if (stepSequencer && header.stepSeqSequences) {
      stepSequencer.loadSequences(header.stepSeqSequences);
   }

   // ---- Reconstruct slicer sequences ----
   if (slicer && header.slicerSequences) {
      // Restore audioBuffers for slicer sequences
      const seqs = header.slicerSequences.map(s => {
         const seq = { ...s };
         if (seq.sampleId && audioBuffers.has(seq.sampleId)) {
            seq.audioBuffer = audioBuffers.get(seq.sampleId);
         }
         return seq;
      });
      slicer.loadSequences(seqs);
   }

   // ---- Reconstruct piano roll patterns ----
   if (pianoRoll && header.pianoRollPatterns) {
      const pats = header.pianoRollPatterns.map(p => {
         const pat = { ...p };
         if (pat.sampleId && audioBuffers.has(pat.sampleId)) {
            pat.audioBuffer = audioBuffers.get(pat.sampleId);
         }
         return pat;
      });
      pianoRoll.loadPatterns(pats);
   }

   // ---- Link timeline MIDI clips to editor live references by UID ----
   // The timeline events and the editor sequences/patterns are loaded as
   // separate copies. Replace the timeline's copies with live references
   // to the editor's objects so that edits in the editors propagate to
   // the timeline in real-time (same behavior as drag-and-drop).
   if (timeline && timeline.state.tracks) {
      // Build UID → live reference maps from the editors.
      // getSequences()/getPatterns() return copies, but getSequenceRef(i)/
      // getPatternRef(i) return the live objects. We use the copies to get
      // the UID at each index, then get the live reference at that index.
      const stepSeqByUid = new Map();
      const slicerByUid = new Map();
      const pianoRollByUid = new Map();
      if (stepSequencer && stepSequencer.getSequences && stepSequencer.getSequenceRef) {
         const copies = stepSequencer.getSequences();
         for (let i = 0; i < copies.length; i++) {
            if (copies[i] && copies[i].uid) stepSeqByUid.set(copies[i].uid, stepSequencer.getSequenceRef(i));
         }
      }
      if (slicer && slicer.getSequences && slicer.getSequenceRef) {
         const copies = slicer.getSequences();
         for (let i = 0; i < copies.length; i++) {
            if (copies[i] && copies[i].uid) slicerByUid.set(copies[i].uid, slicer.getSequenceRef(i));
         }
      }
      if (pianoRoll && pianoRoll.getPatterns && pianoRoll.getPatternRef) {
         const copies = pianoRoll.getPatterns();
         for (let i = 0; i < copies.length; i++) {
            if (copies[i] && copies[i].uid) pianoRollByUid.set(copies[i].uid, pianoRoll.getPatternRef(i));
         }
      }
      for (const tlTrack of timeline.state.tracks) {
         for (const ev of tlTrack.events) {
            if (ev.type === 'stepseq' && ev.sequence && ev.sequence.uid) {
               const live = stepSeqByUid.get(ev.sequence.uid);
               if (live) ev.sequence = live;
            } else if (ev.type === 'slicer' && ev.sequence && ev.sequence.uid) {
               const live = slicerByUid.get(ev.sequence.uid);
               if (live) ev.sequence = live;
            } else if (ev.type === 'pianoroll' && ev.pattern && ev.pattern.uid) {
               const live = pianoRollByUid.get(ev.pattern.uid);
               if (live) ev.pattern = live;
            }
         }
      }
   }

   // ---- Sync engine events from timeline ----
   if (engine && timeline) {
      // Call the sync function if available
      if (opts.syncEngineEvents) opts.syncEngineEvents();
   }

   // ---- Refresh UI ----
   if (timeline) timeline.render();
   if (mixer && opts.getMixerTracks) mixer.setTracks(opts.getMixerTracks());
   if (trackList && trackList.render) trackList.render();
   if (stepSequencer && stepSequencer.render) stepSequencer.render();
   if (slicer && slicer.render) slicer.render();
   if (pianoRoll && pianoRoll.render) pianoRoll.render();

   return header;
}

// ---- Helper: create a download for the song file ----
function downloadSong(data, filename) {
   // data can be a Blob (from saveSong) or an ArrayBuffer
   const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/octet-stream' });
   const url = URL.createObjectURL(blob);
   const a = document.createElement('a');
   a.href = url;
   a.download = filename || 'song.dws';
   document.body.appendChild(a);
   a.click();
   document.body.removeChild(a);
   setTimeout(() => URL.revokeObjectURL(url), 1000);
}

module.exports = { saveSong, loadSong, downloadSong, DWS_MAGIC, DWS_VERSION };
